/**
 * 运行规则（`#guards`）：运行时代码规则注册表 `plan_lab/rule_registry.py` 只读展示（用户 10-15：规则管理页面要能看到）。
 * 按环节 ／ 类型 ／ 机制筛（机制＝摸底 T3 合成的通用机制 M1～M16，空＝独立规则）；点一行看全部字段。可配的规则带当前生效快照里的值，改值去「提示词与模型」等配置页存新快照；
 * 改规则本身走 PR，同 PR 改注册表。
 * 查询参数 `rule`（批 4）：别的页（如信号识别）点规则编号跳过来，直接打开那条的抽屉；关抽屉时去掉参数。
 * 适用范围与依赖（批 5a，用户 10-20 第 11 条）：每条规则标 通用 ／ 电商 ／ 特定平台 ／ 特定工具 与 依赖配置 ／ 写死在代码 ／ 不依赖平台，
 * 可按两项筛、可只看写死的平台 ／ 工具规则；抽屉列出写死了什么、在哪、建议挪到哪（批 5b 按它抽离）。
 * 列宽（10-21 A4；层列加入后「做什么」收到 140）：长文本列（名称 ／ 做什么 ／ 可配 ／ 留痕）把上限写在单元格里的文字上（表格单元格的 max-width 不生效），
 * 超出省略号、悬停看全文；1440 宽下列表不横滚。
 * 批 5b：抽进配置的规则改为「依赖配置」；抽屉加「读的业务参数」（去业务参数页）与「读的契约字段」（去字段契约页）。
 */
import { useNavigate } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";

import { Badge, type BadgeTone } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Drawer, DrawerSection } from "@/components/ui/drawer";
import { EmptyState, LoadingInline } from "@/components/ui/feedback";
import { LabeledSelect } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import {
	type PlanLayer,
	type PlanRule,
	type PlanRuleTag,
	planErrorMessage,
	usePlanRules,
} from "@/lib/api/plan-lab";

import { FailState } from "../kit/fail-state";
import {
	LAYER_ALL,
	LAYER_FALLBACK,
	LayerBadge,
	LayerDetail,
	layerOptions,
	matchLayer,
} from "../kit/layer";
import { PageJump } from "../kit/page-jump";
import { type PlanColumn, PlanTable } from "../kit/plan-table";

const ALL = "__all__";
const NONE = "__none__";

const KIND_TONE: Record<string, BadgeTone> = {
	安全底线: "danger",
	替模型判断: "warning",
	输入取舍: "info",
	事实加工: "muted",
	记账: "neutral",
};

/** 范围徽标色：平台 ／ 工具用醒目色（要抽离的在这两类里） */
const SCOPE_TONE: Record<string, BadgeTone> = {
	general: "muted",
	ecommerce: "info",
	platform: "warning",
	tool: "danger",
};

/** 接口没带取值表时（旧接口 ／ 打桩数据）的兜底中文名 */
const SCOPE_FALLBACK: PlanRuleTag[] = [
	{ id: "general", name: "通用", desc: "" },
	{ id: "ecommerce", name: "电商", desc: "" },
	{ id: "platform", name: "特定平台", desc: "" },
	{ id: "tool", name: "特定工具", desc: "" },
];
const BINDING_FALLBACK: PlanRuleTag[] = [
	{ id: "config", name: "依赖配置", desc: "" },
	{ id: "hardcoded", name: "写死在代码", desc: "" },
	{ id: "none", name: "不依赖平台", desc: "" },
];

function configText(r: PlanRule): string {
	if (!r.config) return "—";
	const v = r.config_value;
	return `${r.config} = ${v === null || v === undefined ? "（未设）" : JSON.stringify(v)}`;
}

/** 写死的平台 ／ 工具规则：开关和顶部统计同一口径 */
function isHardPT(r: PlanRule): boolean {
	return r.binding === "hardcoded" && (r.scope === "platform" || r.scope === "tool");
}

function byId(tags: PlanRuleTag[]): Record<string, PlanRuleTag> {
	return Object.fromEntries(tags.map((t) => [t.id, t]));
}

function columns(
	scopeOf: Record<string, PlanRuleTag>,
	targetText: (r: PlanRule) => string,
	layers: PlanLayer[],
): PlanColumn<PlanRule>[] {
	return [
		{
			key: "id",
			title: "编号",
			render: (r) => <span className="font-mono">{r.id}</span>,
		},
		{ key: "stage", title: "环节", className: "w-20", render: (r) => r.stage },
		{
			key: "scope",
			title: "范围",
			className: "w-24",
			render: (r) =>
				r.scope ? (
					<span title={targetText(r) || undefined} data-testid="plan-rule-scope-badge">
						<Badge tone={SCOPE_TONE[r.scope] ?? "muted"}>{scopeOf[r.scope]?.name ?? r.scope}</Badge>
					</span>
				) : (
					"—"
				),
		},
		{
			key: "layer",
			title: "层",
			className: "w-24",
			render: (r) => <LayerBadge row={r} layers={layers} />,
		},
		{
			key: "mechanism",
			title: "机制",
			className: "w-16",
			render: (r) => (r.mechanism ? <span className="font-mono">{r.mechanism}</span> : "—"),
		},
		{
			key: "name",
			title: "名称",
			render: (r) => (
				<span className="block max-w-[120px] truncate" title={r.name}>
					{r.name}
				</span>
			),
		},
		{
			key: "does",
			title: "做什么",
			render: (r) => (
				<span className="block max-w-[140px] truncate text-text-secondary" title={r.does}>
					{r.does}
				</span>
			),
		},
		{
			key: "kind",
			title: "类型",
			className: "w-24",
			render: (r) => <Badge tone={KIND_TONE[r.kind] ?? "muted"}>{r.kind}</Badge>,
		},
		{
			key: "config",
			title: "可配",
			render: (r) => (
				<span
					className="block max-w-[104px] truncate font-mono text-xs text-text-muted"
					title={configText(r)}
				>
					{configText(r)}
				</span>
			),
		},
		{
			key: "trace",
			title: "留痕",
			render: (r) => (
				<span className="block max-w-[80px] truncate text-xs text-text-muted" title={r.trace}>
					{r.trace || "无"}
				</span>
			),
		},
	];
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
	return (
		<div className="space-y-1">
			<div className="text-xs text-text-muted">{label}</div>
			<div className="whitespace-pre-wrap text-sm text-text-primary">{children}</div>
		</div>
	);
}

export function RulesTab({
	focusId,
	stageFilter,
}: { focusId?: string; stageFilter?: string } = {}) {
	const q = usePlanRules();
	const navigate = useNavigate();
	const [stage, setStage] = useState(stageFilter || ALL);
	// 从链路总览每步的「规则 N 条」跳进来：按那一步的环节筛选；再点一次别的环节照常换
	useEffect(() => {
		if (stageFilter) setStage(stageFilter);
	}, [stageFilter]);
	const [kind, setKind] = useState(ALL);
	const [mech, setMech] = useState(ALL);
	const [scope, setScope] = useState(ALL);
	const [binding, setBinding] = useState(ALL);
	const [layer, setLayer] = useState(LAYER_ALL);
	const [showHistory, setShowHistory] = useState(false);
	const [hardOnly, setHardOnly] = useState(false);
	const [open, setOpen] = useState<PlanRule | null>(null);
	const data = q.data;
	useEffect(() => {
		if (!focusId || !data) return;
		const hit = data.rules.find((r) => r.id === focusId);
		if (hit) setOpen(hit);
	}, [focusId, data]);
	const close = () => {
		setOpen(null);
		setShowHistory(false);
		if (focusId) navigate({ to: "/plan-lab", search: {}, hash: "guards", replace: true });
	};
	const scopes = data?.scopes?.length ? data.scopes : SCOPE_FALLBACK;
	const bindings = data?.bindings?.length ? data.bindings : BINDING_FALLBACK;
	const scopeOf = useMemo(() => byId(scopes), [scopes]);
	const bindingOf = useMemo(() => byId(bindings), [bindings]);
	const extractOf = useMemo(() => byId(data?.extract_to ?? []), [data]);
	const targetText = useMemo(() => {
		const labels = data?.target_labels ?? {};
		return (r: PlanRule) => (r.scope_target ?? []).map((t) => labels[t] ?? t).join("、");
	}, [data]);
	const layers = data?.layers?.length ? data.layers : LAYER_FALLBACK;
	const cols = useMemo(() => columns(scopeOf, targetText, layers), [scopeOf, targetText, layers]);
	const rows = useMemo(
		() =>
			(data?.rules ?? []).filter(
				(r) =>
					(stage === ALL || r.stage === stage) &&
					(kind === ALL || r.kind === kind) &&
					(mech === ALL || (r.mechanism || NONE) === mech) &&
					(scope === ALL || r.scope === scope) &&
					(binding === ALL || r.binding === binding) &&
					matchLayer(layer, r, layers) &&
					(!hardOnly || isHardPT(r)),
			),
		[data, stage, kind, mech, scope, binding, layer, layers, hardOnly],
	);
	const stats = useMemo(() => {
		const all = data?.rules ?? [];
		return {
			by: scopes.map((s) => ({ ...s, n: all.filter((r) => r.scope === s.id).length })),
			hard: all.filter(isHardPT).length,
			layers: layers.map((l) => ({ ...l, n: all.filter((r) => r.layer === l.id).length })),
			unmarked: all.filter((r) => !layers.some((l) => l.id === r.layer)).length,
		};
	}, [data, scopes, layers]);

	// 三态都打一级块标记 plan-block-rules（布局量测 C1～C3 靠它找块；原先只在列表态打了 plan-rules，失败态没有任何一级块）
	if (q.isLoading)
		return (
			<Card data-testid="plan-block-rules">
				<CardContent className="py-4">
					<LoadingInline />
				</CardContent>
			</Card>
		);
	if (q.isError || !data)
		return (
			<Card data-testid="plan-block-rules">
				<CardContent>
					<FailState
						reason={planErrorMessage(q.error)}
						onRetry={() => q.refetch()}
						retrying={q.isFetching}
						testId="plan-rules-fail"
					/>
				</CardContent>
			</Card>
		);

	const opts = (xs: string[]) => [
		{ value: ALL, label: "全部" },
		...xs.map((x) => ({ value: x, label: x })),
	];
	const tagOpts = (xs: PlanRuleTag[]) => [
		{ value: ALL, label: "全部" },
		...xs.map((x) => ({ value: x.id, label: x.name })),
	];
	const openTargets = open ? targetText(open) : "";
	return (
		<Card data-testid="plan-block-rules">
			<CardContent className="space-y-3 py-4" data-testid="plan-rules">
				<div className="flex flex-wrap items-center gap-3">
					<LabeledSelect
						label="环节"
						value={stage}
						onChange={setStage}
						options={opts(data.stages)}
					/>
					<LabeledSelect label="类型" value={kind} onChange={setKind} options={opts(data.kinds)} />
					<LabeledSelect
						label="机制"
						value={mech}
						onChange={setMech}
						options={[
							{ value: ALL, label: "全部" },
							...(data.mechanisms ?? []).map((m) => ({ value: m.id, label: `${m.id} ${m.name}` })),
							{ value: NONE, label: "独立规则" },
						]}
					/>
					<LabeledSelect
						label="适用范围"
						value={scope}
						onChange={setScope}
						options={tagOpts(scopes)}
					/>
					<LabeledSelect
						label="依赖"
						value={binding}
						onChange={setBinding}
						options={tagOpts(bindings)}
					/>
					<span data-testid="plan-rules-layer-filter">
						<LabeledSelect
							label="层"
							value={layer}
							onChange={setLayer}
							options={layerOptions(layers, data.rules)}
						/>
					</span>
					<span className="flex items-center gap-2 text-xs text-text-secondary">
						<Switch
							checked={hardOnly}
							onCheckedChange={setHardOnly}
							aria-label="只看写死的平台／工具规则"
							data-testid="plan-rules-hard-only"
						/>
						只看写死的平台／工具规则
					</span>
					<span className="ml-auto text-xs text-text-muted">共 {rows.length} 条</span>
				</div>
				<div className="text-xs text-text-secondary" data-testid="plan-rules-scope-stats">
					{stats.by.map((s, i) => (
						<span key={s.id}>
							{i ? "、" : ""}
							{s.name} <span className="font-medium text-text-primary">{s.n}</span>
						</span>
					))}
					<span>
						；写死的平台／工具规则{" "}
						<span className="font-medium text-text-primary">{stats.hard}</span> 条
					</span>
				</div>
				<div className="text-xs text-text-secondary" data-testid="plan-rules-layer-stats">
					层：
					{stats.layers
						.filter((l) => l.n)
						.map((l, i) => (
							<span key={l.id}>
								{i ? "、" : ""}
								{l.id} <span className="font-medium text-text-primary">{l.n}</span>
							</span>
						))}
					{stats.unmarked ? (
						<span>
							、未标 <span className="font-medium text-text-primary">{stats.unmarked}</span>
						</span>
					) : null}
				</div>
				{rows.length ? (
					<PlanTable
						columns={cols}
						rows={rows}
						rowKey={(r) => r.id}
						onRowClick={setOpen}
						density="compact"
						rowTestId="plan-rule-row"
					/>
				) : (
					<EmptyState title="没有符合条件的规则" desc="换个筛选条件看看。" />
				)}
			</CardContent>
			<Drawer
				open={!!open}
				onClose={close}
				width="min(720px,95vw)"
				testId="plan-rule-drawer"
				title={
					<span>
						<span className="font-mono">{open?.id}</span> {open?.name}
					</span>
				}
				description={open ? `${open.stage} · ${open.kind}` : undefined}
			>
				{open ? (
					<>
						<DrawerSection title="规则说明">
							<Field label="做什么">{open.does}</Field>
							<Field label="依赖的业务数据">{open.depends || "无"}</Field>
						</DrawerSection>
						<DrawerSection title="层" testId="plan-rule-layer">
							<LayerDetail row={open} layers={layers} />
						</DrawerSection>
						{open.history ? (
							<DrawerSection title="变更史" testId="plan-rule-history">
								<Button
									variant="ghost"
									size="sm"
									onClick={() => setShowHistory((v) => !v)}
									data-testid="plan-rule-history-toggle"
								>
									{showHistory ? "收起" : `展开（${open.history.split("\n").length} 条）`}
								</Button>
								{showHistory ? (
									<div
										className="whitespace-pre-wrap text-xs text-text-secondary"
										data-testid="plan-rule-history-body"
									>
										{open.history}
									</div>
								) : null}
							</DrawerSection>
						) : null}
						<DrawerSection title="适用范围与依赖" testId="plan-rule-scope">
							<Field label="范围">
								{open.scope ? (
									<span className="flex flex-wrap items-center gap-2">
										<Badge tone={SCOPE_TONE[open.scope] ?? "muted"}>
											{scopeOf[open.scope]?.name ?? open.scope}
										</Badge>
										<span className="text-xs text-text-muted">{scopeOf[open.scope]?.desc}</span>
									</span>
								) : (
									"未标"
								)}
							</Field>
							<Field label="对象">{openTargets || "—"}</Field>
							<Field label="依赖方式">
								{open.binding ? (
									<span>
										{bindingOf[open.binding]?.name ?? open.binding}
										{bindingOf[open.binding]?.desc ? (
											<span className="text-xs text-text-muted">
												{"　"}
												{bindingOf[open.binding]?.desc}
											</span>
										) : null}
									</span>
								) : (
									"未标"
								)}
							</Field>
							<Field label="读的业务参数">
								{open.params?.length ? (
									<span className="space-y-1" data-testid="plan-rule-params">
										<span className="block font-mono text-xs">{open.params.join("、")}</span>
										<span className="block text-xs text-text-muted">
											含义与当前值见 <PageJump to="business-params" />
										</span>
									</span>
								) : (
									"无"
								)}
							</Field>
							<Field label="读的契约字段">
								{open.fields?.length ? (
									<span className="space-y-1" data-testid="plan-rule-fields">
										<span className="block font-mono text-xs">{open.fields.join("、")}</span>
										<span className="block text-xs text-text-muted">
											给哪些环节、是否敏感见 <PageJump to="field-contract" />
										</span>
									</span>
								) : (
									"无"
								)}
							</Field>
							<Field label="写死项">
								{open.hardcoded?.length ? (
									<ul className="space-y-2">
										{open.hardcoded.map((h) => (
											<li
												key={`${h.where}-${h.what}`}
												className="space-y-1 rounded-md border border-surface-border p-2"
												data-testid="plan-rule-hardcoded-item"
											>
												<div>{h.what}</div>
												<div className="font-mono text-xs text-text-muted">{h.where}</div>
												<div className="text-xs text-text-secondary">
													建议挪到：
													<span className="font-medium text-text-primary">
														{extractOf[h.extract_to]?.name ?? h.extract_to}
													</span>
													{h.note ? `（${h.note}）` : ""}
												</div>
											</li>
										))}
									</ul>
								) : (
									"无"
								)}
							</Field>
						</DrawerSection>
						<DrawerSection title="配置与留痕">
							<Field label="可配">
								{configText(open)}
								{open.config ? (
									<span className="ml-2 text-xs text-text-muted">
										取生效快照的值，历次快照见 <PageJump to="config-versions" />
									</span>
								) : null}
							</Field>
							<Field label="留痕">{open.trace || "无"}</Field>
							<Field label="代码位置">
								<span className="font-mono text-xs">{open.where}</span>
							</Field>
						</DrawerSection>
					</>
				) : null}
			</Drawer>
		</Card>
	);
}
